import React from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { avatarUrlWithFallback } from "views_shared/app/javascript/utils/avatars";

// Overlapping circular avatars with a "+N" overflow pill. Generalised from
// my_inbox/ConversationProfileImages (also frontend) for the feed liker stack;
// promote to views_shared if a non-frontend consumer appears.
//
// Two optional extras for callers outside the feed:
//   * `avatars[].href` turns an avatar into a link, using its `name` as the accessible name;
//   * `total` reports the real population when the caller has already capped `avatars` itself,
//     so the pill counts the people it left out rather than the ones it handed over.
// The pill is never interactive.
//
// The cluster WRAPS rather than overflowing its container: a profile rail is much narrower than the
// feed, and eight 40px avatars plus a pill do not fit one at the `md` breakpoint. Every avatar carries
// the negative overlap margin (not just the ones after the first) and the row cancels the leading one
// with matching padding, so an avatar starting a wrapped row still lines up with the row above it.
function AvatarStack({ avatars, size = 28, max = 3, total = null }) {
  const valid = (avatars || []).filter(Boolean);
  if (valid.length === 0) return null;

  const shown = valid.slice(0, max);
  const population = total == null ? valid.length : total;
  const overflow = Math.max(0, population - shown.length);
  const overlap = Math.round(size * 0.35);

  return (
    <div
      className="d-flex flex-wrap align-items-center"
      style={{ paddingLeft: overlap, rowGap: Math.round(size * 0.15) }}
    >
      {shown.map((avatar, index) => {
        const image = (
          <ImageCropped
            roundedCircle
            src={avatarUrlWithFallback(avatar.avatarUrl, avatar.name)}
            alt={avatar.name || ""}
            size={size}
            className="border border-white border-2"
            fallbackToInitials
          />
        );

        return (
          <div
            key={avatar.key ?? index}
            style={{ marginLeft: -overlap, zIndex: shown.length - index, position: "relative" }}
          >
            {avatar.href ? (
              // The image alone carries no accessible name a screen reader can announce as a
              // destination, so name the link itself; `title` gives sighted users the same hover hint.
              <a
                href={avatar.href}
                className="d-inline-block"
                aria-label={avatar.name || undefined}
                title={avatar.name || undefined}
              >
                {image}
              </a>
            ) : (
              image
            )}
          </div>
        );
      })}
      {overflow > 0 && (
        <div
          className="d-flex align-items-center justify-content-center rounded-circle bg-neutral-20 text-neutral-80 fw-bold border border-white border-2 paragraph-xs"
          style={{ width: size, height: size, marginLeft: -overlap, position: "relative", zIndex: 0 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

AvatarStack.propTypes = {
  avatars: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      avatarUrl: PropTypes.string,
      href: PropTypes.string,
    }),
  ),
  size: PropTypes.number,
  max: PropTypes.number,
  total: PropTypes.number,
};

export default AvatarStack;
